import {
  SPLIT_BRAND_LEVEL_UNRESOLVED,
  type AisleBreakBrandLevelOption,
  type SplitBrandLevelUnresolvedDetail,
} from "@CommonApi/categories/_categoryId_/aisle-break-brand-level";
import type { OneStoreViewRow } from "@CommonTypes/merchflow/OneStoreView";
import { isAxiosError } from "axios";
import { useState } from "react";
import {
  useSaveAisleBreakBrandLevelMutation,
  useStoreGenerationRunRowsQuery,
} from "src/api/aisleBreaks";
import { Button, Color, Flex, Radios, Text } from "src/elements";
import { useTranslation } from "src/i18n";

/** The refused stores of one base pog, which the page's store generation can run again. */
export interface UnresolvedBrandLevelStores {
  basePogId: number;
  storeCodes: string[];
}

export interface UnresolvedBrandLevel {
  detail: SplitBrandLevelUnresolvedDetail;
  storeCount: number;
  stores: UnresolvedBrandLevelStores[];
  /** The store generation runs (store pog triggers) that refused them. */
  runIds: number[];
}

export const unresolvedBrandLevelOf = (
  rows: readonly OneStoreViewRow[],
): UnresolvedBrandLevel | null => {
  const refused = rows.flatMap((row) => {
    const detail = row.store_pog?.error_detail;
    return detail?.code === SPLIT_BRAND_LEVEL_UNRESOLVED ? [{ row, detail }] : [];
  });
  if (refused.length === 0) return null;

  const storeCodesByBasePog = new Map<number, string[]>();
  for (const { row } of refused) {
    if (row.base_pog_id === undefined) continue;
    storeCodesByBasePog.set(row.base_pog_id, [
      ...(storeCodesByBasePog.get(row.base_pog_id) ?? []),
      row.store,
    ]);
  }

  return {
    detail: refused[0].detail,
    // The page shows one page of rows; the detail counts the whole run.
    storeCount: Math.max(
      refused.length,
      ...refused.map(({ detail }) => detail.affected_store_count),
    ),
    stores: [...storeCodesByBasePog].map(([basePogId, storeCodes]) => ({ basePogId, storeCodes })),
    runIds: [...new Set(refused.map(({ row }) => row.store_pog.store_pog_trigger_id))].sort(
      (left, right) => left - right,
    ),
  };
};

type SaveRefusal = "noStudioProfile" | "noAttributeNamesLevel" | "changed" | "failed";

const refusalOf = (error: unknown): SaveRefusal => {
  if (!isAxiosError(error)) return "failed";
  if (error.response?.status === 409) return "changed";
  const data: unknown = error.response?.data;
  const reason =
    typeof data === "object" && data !== null && "reason" in data ? data.reason : undefined;
  if (error.response?.status === 422 && reason === "NO_STUDIO_PROFILE") return "noStudioProfile";
  if (error.response?.status === 422 && reason === "NO_ATTRIBUTE_NAMES_LEVEL") {
    return "noAttributeNamesLevel";
  }
  return "failed";
};

interface PickerProps {
  merchflowId: number;
  unresolved: UnresolvedBrandLevel;
  onCdtChanged: () => void;
  onGenerate: (stores: UnresolvedBrandLevelStores) => void;
}

const BrandLevelPicker = ({ merchflowId, unresolved, onCdtChanged, onGenerate }: PickerProps) => {
  const { t } = useTranslation("common");
  const { detail } = unresolved;
  // The rows on screen are one page; the groups to generate are the refused runs' every page,
  // never a part of them: until those are read the buttons wait, and a failed read offers a retry.
  const runRows = useStoreGenerationRunRowsQuery(merchflowId, unresolved.runIds);
  const stores = runRows.data
    ? (unresolvedBrandLevelOf(runRows.data)?.stores ?? [])
    : unresolved.stores;
  const canGenerate = runRows.data !== undefined;
  const save = useSaveAisleBreakBrandLevelMutation();
  const [step, setStep] = useState<"closed" | "picking" | "saved">("closed");
  const [level, setLevel] = useState<AisleBreakBrandLevelOption["level"] | null>(null);
  const [refusal, setRefusal] = useState<SaveRefusal | null>(null);
  const levelName = detail.levels.find((option) => option.level === level)?.name ?? "";

  const onSave = () => {
    if (level === null) return;
    setRefusal(null);
    save.mutate(
      {
        categoryId: detail.category_id,
        payload: { level, release_id: detail.release_id, tree_id: detail.tree_id },
      },
      {
        onSuccess: () => setStep("saved"),
        onError: (error) => {
          const reason = refusalOf(error);
          setRefusal(reason);
          if (reason === "changed") {
            setStep("closed");
            setLevel(null);
            onCdtChanged();
          }
        },
      },
    );
  };

  const generateLabel = (count: number) =>
    count === 1
      ? t("aisleBreaks.brandLevel.generateOne")
      : t("aisleBreaks.brandLevel.generateMany", { count });

  return (
    <Flex column gap="8px">
      {step === "closed" && refusal !== "changed" && (
        <Button size="small" variant="inverted" onClick={() => setStep("picking")}>
          {t("aisleBreaks.brandLevel.setBrandLevel")}
        </Button>
      )}

      {step === "picking" && (
        <Flex column gap="8px">
          <Text variant="body2">{t("aisleBreaks.brandLevel.question")}</Text>

          {detail.levels.length === 0 ? (
            <Text variant="small2" color={Color.textSecondary}>
              {t("aisleBreaks.brandLevel.noLevels")}
            </Text>
          ) : (
            <Radios
              direction="column"
              value={level}
              setValue={setLevel}
              options={detail.levels.map((option) => ({ label: option.name, value: option.level }))}
              isDisabled={save.isPending}
            />
          )}

          <Flex gap="8px">
            <Button
              size="small"
              isDisabled={level === null}
              isLoading={save.isPending}
              onClick={onSave}
            >
              {t("aisleBreaks.brandLevel.save")}
            </Button>

            <Button
              size="small"
              variant="inverted"
              isDisabled={save.isPending}
              onClick={() => setStep("closed")}
            >
              {t("aisleBreaks.brandLevel.cancel")}
            </Button>
          </Flex>
        </Flex>
      )}

      {step === "saved" && (
        <Text variant="small2" color={Color.primaryActive}>
          {t("aisleBreaks.brandLevel.saved", { name: levelName })}
        </Text>
      )}

      {/* After a 409 the refused run still names the old CDT, so only a new run can ask again. */}
      {(step === "saved" || refusal === "changed") && (
        <Flex gap="8px">
          {stores.map((group) => (
            <Button
              key={group.basePogId}
              size="small"
              isLoading={runRows.isLoading}
              isDisabled={!canGenerate}
              onClick={() => onGenerate(group)}
            >
              {generateLabel(group.storeCodes.length)}
            </Button>
          ))}
        </Flex>
      )}

      {(step === "saved" || refusal === "changed") && runRows.isError && (
        <Flex align="center" gap="8px">
          <Text variant="small2" color={Color.red}>
            {t("aisleBreaks.brandLevel.runReadFailed")}
          </Text>
          <Button size="small" variant="inverted" onClick={() => void runRows.refetch()}>
            {t("aisleBreaks.brandLevel.retry")}
          </Button>
        </Flex>
      )}

      {refusal && (
        <Text variant="small2" color={Color.red}>
          {refusal === "noAttributeNamesLevel"
            ? t("aisleBreaks.brandLevel.noAttributeNamesLevel", { name: levelName })
            : t(`aisleBreaks.brandLevel.${refusal}`)}
        </Text>
      )}
    </Flex>
  );
};

interface Props {
  merchflowId: number;
  /** The page on screen; the picker reads the refused runs in full. */
  rows: readonly OneStoreViewRow[];
  /** MERCHANT_SETTINGS_UPDATE: the pick is saved to the merchant's CDT Studio profile. */
  canSetBrandLevel: boolean;
  onCdtChanged: () => void;
  onGenerate: (stores: UnresolvedBrandLevelStores) => void;
}

export const AisleBreakBrandLevelNotice = ({
  merchflowId,
  rows,
  canSetBrandLevel,
  onCdtChanged,
  onGenerate,
}: Props) => {
  const { t } = useTranslation("common");
  const unresolved = unresolvedBrandLevelOf(rows);
  if (!unresolved) return null;

  return (
    <Flex column gap="8px" padding="0 10px 10px 10px">
      <Text color={Color.yellow}>
        {unresolved.storeCount === 1
          ? t("aisleBreaks.brandLevel.unresolvedOne")
          : t("aisleBreaks.brandLevel.unresolvedMany", { count: unresolved.storeCount })}
      </Text>

      {canSetBrandLevel && (
        <BrandLevelPicker
          key={`${unresolved.detail.category_id}-${unresolved.detail.release_id}-${unresolved.detail.tree_id}`}
          merchflowId={merchflowId}
          unresolved={unresolved}
          onCdtChanged={onCdtChanged}
          onGenerate={onGenerate}
        />
      )}
    </Flex>
  );
};
